import {
  Box,
  Button,
  chakra,
  Flex,
  IconButton,
  Input,
  Text,
} from "@chakra-ui/react"
import { navigate, useIntl } from "gatsby-plugin-intl"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import OrderIcon from "../../icons/Order"
import {
  COMMON_MESSAGES,
  ERROR_MESSAGES,
  LANDING_MESSAGES,
  ORDER_PAGE_MESSAGES,
} from "../../intl/messages"
import { Portal } from "../Misc/Portal"

const TopSearchButton = chakra(
  ({
    className,
    setExpanded,
    expanded,
    isSticky,
  }: {
    className?: any
    setExpanded: any
    expanded: boolean
    isSticky: boolean
  }) => {
    const intl = useIntl()
    return (
      <Flex
        _expanded={{
          transform: "unset",
          opacity: "1",
        }}
        transform={[
          "scale(1.3, 1.375) translateY(58px)",
          "scale(1.4, 1.375) translateY(58px)",
          "scale(2.3, 1.375) translateY(58px)",
        ]}
        opacity={0}
        onClick={() => {
          setExpanded(exp => !exp)
        }}
        pointerEvents="auto"
        aria-expanded={isSticky && !expanded}
        role="button"
        boxShadow="elevation_2"
        transformOrigin="50% 0%"
        transition="-ms-transform 150ms,-webkit-transform 150ms,transform 150ms, opacity 50ms 20ms, visibility 0ms 25ms !important;"
        _hover={{ boxShadow: "elevation_3" }}
        className={className}
        h={12}
        borderRadius="50px"
        w="100%"
        maxW="350px"
        mx="auto"
        bg="white"
        px={2}
        alignItems="center"
        borderWidth={1}
      >
        <Text
          as="strong"
          color="text.dark"
          pl={3}
          fontSize={["400", "500"]}
          flexGrow={1}
        >
          {intl.formatMessage(COMMON_MESSAGES.shopcheaper)}
        </Text>

        <IconButton
          flexShrink={0}
          h="10"
          d="block"
          w="10"
          borderRadius="50%"
          variant="success"
          type="submit"
          aria-label="Add Order"
          icon={<OrderIcon />}
        />
      </Flex>
    )
  }
)

const AddOrderForm = chakra(
  ({ className, expanded, setExpanded, isSticky }) => {
    const [selected, setSelected] = useState(false)
    const { register, errors, handleSubmit } = useForm()

    const submitHandler = data => {
      try {
        new URL(data.url_name)
        navigate(`/order/add/?order_url=${data.url_name}/`)
      } catch (e) {
        navigate(`/order/add/?title=${data.url_name}`)
      }
    }
    const intl = useIntl()
    const [errorText, setErrorText] = useState(null)
    useEffect(() => {
      if (expanded == false) return
      var firstScroll = window.pageYOffset
      const scrollListener = e => {
        var currentScrollPos = window.pageYOffset
        if (Math.abs(firstScroll - currentScrollPos) > 200) setExpanded(false)
      }

      window.addEventListener("scroll", scrollListener)
      return function () {
        window.removeEventListener("scroll", scrollListener)
      }
    }, [expanded])

    useEffect(() => {
      if (errors.url_name) {
        setErrorText(errors.url_name.message)
        setTimeout(() => {
          setErrorText(null)
        }, 2000)
      }
    }, [errors.url_name])
    return (
      <Box
        as="form"
        onSubmit={handleSubmit(submitHandler)}
        className={className}
        pointerEvents="none"
        aria-expanded={isSticky && !expanded}
        mx="auto"
        w="100%"
        maxW="container.lg"
        autoComplete="off"
      >
        <Portal>
          <Box
            onClick={() => {
              setExpanded(false)
            }}
            transition="-ms-transform 150ms,-webkit-transform 150ms,transform 150ms, opacity 50ms 20ms, visibility 0ms 25ms !important;"
            aria-expanded={expanded}
            _expanded={{ d: "block" }}
            d="none"
            h="100vh"
            w="100%"
            bg="rgba(0,0,0,0.08)"
            pos="fixed"
          ></Box>
        </Portal>
        <Flex justifyContent="center" alignItems="center" w="100%" h="55px">
          <Text color="text.black" textAlign="center" pl={3} fontWeight="700">
            {intl.formatMessage(LANDING_MESSAGES.createorderforfree)}
          </Text>
        </Flex>
        <Flex
          aria-expanded={isSticky && !expanded}
          pointerEvents="auto"
          _expanded={{ pointerEvents: "none" }}
          cursor="pointer"
          alignItems="stretch"
          boxShadow="elevation_4"
          borderWidth="1px"
          borderColor="outline.medium"
          bg="white"
          borderRadius="2rem"
          w="100%"
          mt={6}
        >
          <Box
            _hover={{ bg: "outline.light" }}
            borderRadius="2rem"
            aria-selected={selected}
            backgroundClip="padding-box"
            _selected={{ boxShadow: "rgb(0 0 0 / 20%) 0px 6px 20px" }}
            flex={1}
            px="2rem"
            py="8px"
          >
            <Text
              fontSize="400"
              fontWeight="600"
              color={errorText ? "danger.base" : "text.dark"}
              as="label"
            >
              {errorText ||
                intl.formatMessage(ORDER_PAGE_MESSAGES.urlornameofproduct)}
            </Text>
            <Input
              _focus={{ fontSize: 600 }}
              id="add_order_form_input"
              variant="unstyled"
              color="text.dark"
              fontSize="500"
              onBlur={() => {
                setSelected(false)
              }}
              onFocus={() => {
                setSelected(true)
              }}
              placeholder="https://www.example.com"
              p={0}
              border="none"
              ref={register({
                required: intl.formatMessage(ERROR_MESSAGES.producturlrequired),
              })}
              name="url_name"
            />
          </Box>
          <Button
            fontWeight="700"
            h="calc(3rem + 2px)"
            w="calc(3rem + 2px)"
            p={0}
            alignSelf="center"
            mr={2}
            variant="success"
            type="submit"
            borderRadius="50%"
          >
            <OrderIcon mx="auto" fontSize="1.2em" />
          </Button>
        </Flex>
      </Box>
    )
  }
)

export const AddOrderNavigationMenu = ({ expanded, setExpanded, isSticky }) => {
  return (
    <>
      <Box flex="0 1 320px" mx="auto">
        <TopSearchButton
          expanded={expanded}
          setExpanded={setExpanded}
          isSticky={isSticky}
        />
        <Box
          transition="-ms-transform 150ms,-webkit-transform 150ms,transform 150ms, opacity 50ms 20ms, visibility 0ms 25ms !important;"
          top={0}
          _expanded={{
            opacity: 0,
            transform: "scale(0.35, 0.727273) translateY(-58px)",
          }}
          pos="absolute"
          px={[0, 0, 5]}
          transformOrigin="50% 0%"
          left="0"
          right="0"
          pointerEvents="none"
          zIndex={2}
          aria-expanded={isSticky && !expanded}
        >
          <Box>
            <Box zIndex={1} pb={6}>
              <AddOrderForm
                expanded={expanded}
                setExpanded={setExpanded}
                isSticky={isSticky}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
